import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  setUserOnline,
  setUserOffline,
  PRESENCE_HEARTBEAT_MS,
} from '../firebase/presenceService';

/**
 * App-level presence writer while authenticated.
 * Honors privacySettings.showOnlineStatus from userProfile.
 */
export default function usePresenceHeartbeat() {
  const { currentUser, userProfile } = useAuth();
  const showOnline =
    userProfile?.privacySettings?.showOnlineStatus !== false;
  const showOnlineRef = useRef(showOnline);
  showOnlineRef.current = showOnline;

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const uid = currentUser.uid;
    let heartbeatTimer = null;
    let cancelled = false;

    const beat = () => {
      if (cancelled) return;
      setUserOnline(uid, { showOnlineStatus: showOnlineRef.current });
    };

    const startHeartbeat = () => {
      beat();
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = setInterval(beat, PRESENCE_HEARTBEAT_MS);
    };

    const stopHeartbeat = (markOffline) => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      if (markOffline) {
        setUserOffline(uid);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        startHeartbeat();
      } else {
        // Pause heartbeats; leave lastSeen — TTL will drop online soon.
        // Best-effort offline so others see quicker leave.
        stopHeartbeat(true);
      }
    };

    const onPageHide = () => {
      stopHeartbeat(true);
    };

    startHeartbeat();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      stopHeartbeat(true);
    };
  }, [currentUser?.uid]);

  // Privacy toggle mid-session
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (showOnline) {
      setUserOnline(currentUser.uid, { showOnlineStatus: true });
    } else {
      setUserOffline(currentUser.uid);
    }
  }, [currentUser?.uid, showOnline]);
}
